import type { Raffle, RaffleWithDetails, PublicRaffle, LiveRaffleData, Participant } from '../types';
import { getAuthToken, clearAuth } from '../components/AdminAuth';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // If unauthorized, clear auth and redirect
    if (response.status === 401) {
      clearAuth();
      window.location.href = '/admin';
      throw new Error('Session expired. Please log in again.');
    }
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  return response.json();
}

// Get auth headers for admin requests
function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Admin API (all requests include auth token)
export const adminApi = {
  getRaffles: async (): Promise<Raffle[]> => {
    const res = await fetch(`${API_BASE}/admin/raffles`, {
      headers: { ...getAuthHeaders() },
    });
    return handleResponse(res);
  },

  getRaffle: async (id: string): Promise<RaffleWithDetails> => {
    const res = await fetch(`${API_BASE}/admin/raffles/${id}`, {
      headers: { ...getAuthHeaders() },
    });
    return handleResponse(res);
  },

  createRaffle: async (name: string, description?: string, maxWinners?: number): Promise<Raffle> => {
    const res = await fetch(`${API_BASE}/admin/raffles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ name, description, maxWinners: maxWinners || 1 }),
    });
    return handleResponse(res);
  },

  updateRaffleStatus: async (id: string, status: string): Promise<Raffle> => {
    const res = await fetch(`${API_BASE}/admin/raffles/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ status }),
    });
    return handleResponse(res);
  },

  deleteRaffle: async (id: string): Promise<void> => {
    const res = await fetch(`${API_BASE}/admin/raffles/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    // handleResponse will handle all error cases including 401
    await handleResponse<void>(res);
  },

  pickWinner: async (id: string, prizeName?: string): Promise<{ winner: { participant: Participant; prize_name: string | null }; remaining_eligible: number; winners_selected: number; max_winners: number; winners_remaining: number; raffle_completed: boolean }> => {
    const res = await fetch(`${API_BASE}/admin/raffles/${id}/pick-winner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ prizeName }),
    });
    return handleResponse(res);
  },

  startSpin: async (id: string, prizeName?: string): Promise<{ winner: { participant: Participant }; eligible_participants: Participant[]; spinning: boolean }> => {
    const res = await fetch(`${API_BASE}/admin/raffles/${id}/start-spin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ prizeName }),
    });
    return handleResponse(res);
  },

  confirmWinner: async (id: string): Promise<{ winner: { participant: Participant; prize_name: string | null }; remaining_eligible: number; winners_selected: number; max_winners: number; winners_remaining: number; raffle_completed: boolean }> => {
    const res = await fetch(`${API_BASE}/admin/raffles/${id}/confirm-winner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    return handleResponse(res);
  },
};

// ETag cache for conditional requests
const etagCache: Map<string, string> = new Map();

// Public API
export const publicApi = {
  getRaffleByCode: async (code: string): Promise<PublicRaffle> => {
    const res = await fetch(`${API_BASE}/raffles/code/${code.toUpperCase()}`);
    return handleResponse(res);
  },

  joinRaffle: async (raffleId: string, firstName: string, lastName: string, email: string): Promise<{ message: string; participant: Participant }> => {
    const res = await fetch(`${API_BASE}/raffles/${raffleId}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName, lastName, email }),
    });
    return handleResponse(res);
  },

  // OPTIMIZED: Uses ETag for conditional requests to reduce bandwidth
  getLiveRaffle: async (id: string): Promise<LiveRaffleData | null> => {
    const cacheKey = `live-${id}`;
    const etag = etagCache.get(cacheKey);

    const headers: HeadersInit = {};
    if (etag) {
      headers['If-None-Match'] = etag;
    }

    const res = await fetch(`${API_BASE}/raffles/${id}/live`, { headers });

    // 304 Not Modified - data hasn't changed
    if (res.status === 304) {
      return null; // Signal to caller to use cached data
    }

    // Store new ETag
    const newEtag = res.headers.get('ETag');
    if (newEtag) {
      etagCache.set(cacheKey, newEtag);
    }

    return handleResponse(res);
  },
};
